'use client';

import { useEffect, useRef, useState } from 'react';
import { useViewportZoom } from '@/lib/viewportZoom';

export default function CustomCursor() {
    const ringRef = useRef<HTMLDivElement>(null);
    const zoomRef = useViewportZoom();
    const [isHovering, setIsHovering] = useState(false);

    useEffect(() => {
        // Position is written straight to the DOM on every mousemove: the ring
        // center is always exactly at the pointer coordinates the browser uses
        // for hover/hit-testing. No springs, no state, no animation library.
        // clientX/Y are viewport px while written px live in the zoomed space
        // of <html zoom>, so divide by z (the -16 half-size stays local).
        const move = (e: MouseEvent) => {
            if (ringRef.current) {
                const z = zoomRef.current;
                ringRef.current.style.transform = `translate3d(${e.clientX / z - 16}px, ${e.clientY / z - 16}px, 0)`;
            }
        };

        const handleMouseOver = (e: MouseEvent) => {
            const t = e.target as HTMLElement;
            if (t.tagName === 'A' || t.tagName === 'BUTTON' || t.closest('a') || t.closest('button')) {
                setIsHovering(true);
            } else {
                setIsHovering(false);
            }
        };

        window.addEventListener('mousemove', move);
        window.addEventListener('mouseover', handleMouseOver);

        return () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseover', handleMouseOver);
        };
    }, [zoomRef]);

    return (
        <div
            ref={ringRef}
            className="fixed top-0 left-0 w-8 h-8 pointer-events-none z-50 hidden md:block will-change-transform"
        >
            {/* Scale-on-hover lives on an inner element so the grow animation
                can never interfere with the outer 1:1 positioning transform. */}
            <div
                className={`w-full h-full border-2 border-primary rounded-full mix-blend-difference flex items-center justify-center transition-transform duration-200 ${isHovering ? 'scale-150' : 'scale-100'}`}
            >
                <div className={`w-2 h-2 bg-primary rounded-full transition-opacity duration-200 ${isHovering ? 'opacity-0' : 'opacity-100'}`} />
            </div>
        </div>
    );
}
